import * as React from 'react';
import { Card, CardContent, type CardProps } from '@/hen-ui/composites/Card';
import { Image } from '@/hen-ui/atomics/Image';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text, type TextProps } from '@/hen-ui/atomics/Text';
import { Brow } from '@/hen-ui/atomics/Brow';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Icon, type IconProps, type IconName } from '@/hen-ui/atomics/Icon';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import {
  infoCardContentVariants,
  infoCardGroupVariants,
  type InfoCardContentVariantProps,
} from './info-card.variants';

/** Call-to-action rendered as a sibling Button below the InfoCard body. */
export type InfoCardCta = {
  /** Visible CTA label. */
  label: string;
  /** Destination URL — renders the Button as an anchor. */
  href: string;
  /** Button variant. Defaults to `textOnly`. */
  variant?: ButtonProps['variant'];
  /** Optional trailing icon (Lucide name, e.g. `arrow-right`). */
  iconAfter?: IconName;
};

export type InfoCardProps = {
  /** Optional image source URL */
  imageSrc?: string;
  /** Optional alt text for the image (defaults to title-based alt if title provided) */
  imageAlt?: string;
  /**
   * Optional Lucide icon rendered above the content group. Matches the Figma
   * "Info Card" with an icon (e.g. Key Findings). Takes precedence over
   * `imageSrc` when both are provided.
   */
  icon?: IconName;
  /** Color token for the icon. Defaults to `heading` (matches the Figma icon color). */
  iconColor?: IconProps['color'];
  /**
   * Color token for the body copy. Defaults to `body`. Use `subtext` to match
   * the Figma "Info Card" body color.
   */
  bodyColor?: TextProps['color'];
  /** Optional uppercase contextual label rendered above the title (Figma "Brow"). */
  brow?: string;
  /** Card title text */
  title?: string;
  /** Card description text or React node */
  children?: React.ReactNode;
  /**
   * Background variant forwarded to the inner `Card`.
   * Defaults to `background-white`.
   */
  variant?: CardProps['variant'];
  /**
   * Padding/gap scale. `default` uses the compact `info-card-*` tokens; `lg`
   * uses the large-format `info-box-lg-*` tokens (Figma "Info Card" spec).
   */
  size?: InfoCardContentVariantProps['size'];
  /**
   * Content alignment. `start` (default) left-aligns; `center` centers the
   * brow, title, body, and CTA — matching the BC About "Our Why" cards.
   */
  align?: InfoCardContentVariantProps['align'];
  /**
   * Optional call-to-action. Rendered as a sibling `Button` after the body so
   * it stays structurally outside the description `<Text>` (valid HTML — no
   * anchor nested in a paragraph).
   */
  cta?: InfoCardCta;
  /** Optional className for the card */
  className?: string;
};

/**
 * InfoCard - Composite component for information cards
 *
 * A composite component that displays information in a card format with optional image,
 * brow, title, description, and call-to-action. Uses Card and CardContent composites with
 * atomic components (Image, Brow, Heading, Text, Button) following HEN Design System standards.
 *
 * Spacing follows the Figma "Info Card": brow, title, body, and CTA share a tight
 * `gap-xxs` rhythm inside a content group, while an optional image is separated
 * from that group by the larger outer gap. Padding follows the `size` prop —
 * `default` uses the compact `info-card-*` tokens, `lg` uses the large-format
 * `info-box-lg-*` tokens.
 *
 * @example
 * ```tsx
 * <InfoCard
 *   variant="background-white"
 *   size="lg"
 *   align="center"
 *   brow="WE BELIEVE"
 *   title="Education is for everyone"
 * >
 *   We celebrate that every journey is unique.
 * </InfoCard>
 * ```
 */
export const InfoCard: React.FC<InfoCardProps> = ({
  imageSrc,
  imageAlt,
  icon,
  iconColor = 'heading',
  bodyColor,
  brow,
  title,
  children,
  variant,
  size,
  align,
  cta,
  className,
}) => {
  const defaultImageAlt = title ? `${title} icon` : 'Card image';
  const textAlign = align === 'center' ? 'center' : 'left';
  const hasGroupContent = Boolean(brow || title || children || cta);

  return (
    <Card
      variant={variant}
      className={cn(className)}
      data-hen-id={COMPONENT_IDS.InfoCard}
    >
      <CardContent
        className={cn(
          infoCardContentVariants({ size, align }),
          icon && 'gap-md',
        )}
      >
        {icon ? (
          <Icon icon={icon} size='4xl' color={iconColor} aria-hidden />
        ) : (
          imageSrc && (
            <Image
              src={imageSrc}
              alt={imageAlt || defaultImageAlt}
              className='w-16 h-16'
            />
          )
        )}
        {hasGroupContent && (
          <div className={cn(infoCardGroupVariants({ size, align }))}>
            {brow && <Brow align={textAlign}>{brow}</Brow>}
            {title && (
              <Heading variant='h3' align={textAlign} className='w-full'>
                {title}
              </Heading>
            )}
            {children && (
              <Text
                variant='b4'
                tag='div'
                color={bodyColor}
                className='w-full'
                align={textAlign}
              >
                {children}
              </Text>
            )}
            {cta && (
              <Button
                href={cta.href}
                variant={cta.variant ?? 'textOnly'}
                iconAfter={cta.iconAfter}
              >
                {cta.label}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

InfoCard.displayName = 'InfoCard';
